import {
	COMPETITION_READ_ROLES,
	COMPETITION_SETUP_ROLES,
	COMPETITION_WRITE_ROLES,
	MEDAL_AWARD_ROLES,
	type MeResponse,
	type RoleCode,
} from "@seagames/contracts";

/**
 * What the ACTIVE role may do in OVR. One active role per session — the
 * roles a user merely holds never count (CLAUDE.md § Auth invariants).
 *
 *   write — COMPETITION_WRITE_ROLES: enter results, run status actions
 *   read  — COMPETITION_READ_ROLES only (an NOC login): every screen, read-only
 *   none  — anyone else: the "no access to results entry" screen — and the
 *           medal desk (MEDAL_AWARD_ROLES, MEDAL_DESK) on purpose: it reads
 *           competition data, but a desk session belongs in MSES. OVR only
 *           SUGGESTS a podium; refusing the desk role here is what keeps every
 *           OVR session unable to award (user, 2026-10-22).
 *
 * The API enforces the same lists on every request; this only says so up front.
 */
export type OvrAccess = "write" | "read" | "none";

const includes = (list: readonly RoleCode[], role: RoleCode | null | undefined): boolean =>
	role != null && list.includes(role);

export function ovrAccessFor(role: RoleCode | null | undefined): OvrAccess {
	if (includes(MEDAL_AWARD_ROLES, role)) return "none";
	if (includes(COMPETITION_WRITE_ROLES, role)) return "write";
	if (includes(COMPETITION_READ_ROLES, role)) return "read";
	return "none";
}

/**
 * "Bulk action" on a sport's meets (`POST /meets/bulk-action`): the setup
 * roles only (SUPER_ADMIN, ADMIN), by the ACTIVE role — the API's own rule.
 * An OPERATION login writes meets one at a time.
 */
export const canBulkMeets = (role: RoleCode | null | undefined): boolean => includes(COMPETITION_SETUP_ROLES, role);

/** Held roles (other than the active one) that WOULD open OVR — for the no-access screen's switcher. */
export function usableOtherRoles(session: MeResponse): RoleCode[] {
	return session.roles
		.map((r) => r.code)
		.filter((code) => code !== session.active_role && ovrAccessFor(code) !== "none");
}
